import type { TOC } from '@ember/component/template-only';
import type { ComponentLike, WithBoundArgs } from '@glint/template';
import { or } from '../utilities.ts';
import { concat } from '@ember/helper';

interface DescriptionGroupSignature {
  Args: {
    striped?: boolean;
    compact?: boolean;
    cols?: 2 | 3 | 4 | 5 | 6; // default 3
    layout?: 'summary';
  };
  Blocks: { default: [] };
  Element: HTMLElement;
}

const gridColsClass = (cols: number = 3) =>
  (
    ({
      2: 'sm:grid-cols-2',
      3: 'sm:grid-cols-3',
      4: 'sm:grid-cols-4',
      5: 'sm:grid-cols-5',
      6: 'sm:grid-cols-6',
    }) as const
  )[cols] ?? 'sm:grid-cols-3';

const detailsSpanClass = (cols: number = 3) =>
  (
    ({
      2: 'sm:col-span-1',
      3: 'sm:col-span-2',
      4: 'sm:col-span-3',
      5: 'sm:col-span-4',
      6: 'sm:col-span-5',
    }) as const
  )[cols] ?? 'sm:col-span-2';

const descriptionGroupClasses = (opts?: {
  striped?: boolean;
  compact?: boolean;
  cols?: number;
  layout?: 'summary';
}) => {
  const { striped, compact, cols = 3, layout } = opts || {};
  if (layout === 'summary') return 'app-description-list__summary-item';
  return [
    compact ? 'p-1' : 'px-4 py-3 sm:px-3',
    'sm:grid',
    gridColsClass(cols),
    'sm:gap-4',
    striped && 'even:tio-bg-sunken',
  ]
    .filter(Boolean)
    .join(' ');
};

const _DescriptionGroup: TOC<DescriptionGroupSignature> = <template>
  <div
    class={{descriptionGroupClasses
      (hash striped=@striped compact=@compact cols=@cols layout=@layout)
    }}
    ...attributes
  >
    {{yield}}
  </div>
</template>;

const DescriptionTerm: TOC<{
  Args: { cols?: 2 | 3 | 4 | 5 | 6; layout?: 'summary' };
  Element: HTMLElement;
  Blocks: { default: [] };
}> = <template>
  <dt
    class={{if @layout "app-description-list__summary-term" "tio-text-sm-medium leading-6 min-w-0"}}
    ...attributes
  >
    {{yield}}
  </dt>
</template>;

const DescriptionDetails: TOC<{
  Args: { cols?: 2 | 3 | 4 | 5 | 6; layout?: 'summary' };
  Element: HTMLElement;
  Blocks: { default: [] };
}> = <template>
  <dd
    class={{if
      @layout
      "app-description-list__summary-value"
      (concat (detailsSpanClass (or @cols 3)) " mt-1 text-sm leading-6 tio-fg-secondary sm:mt-0")
    }}
    ...attributes
  >
    {{yield}}
  </dd>
</template>;

interface DescriptionListSignature {
  Args: {
    striped?: boolean;
    compact?: boolean;
    divided?: boolean;
    cols?: 2 | 3 | 4 | 5 | 6; // default 3
    /** Two compact fact columns below 64rem, four above; row-only cols/compact/striped/divided options do not apply. */
    layout?: 'summary';
  };
  Blocks: {
    default: [
      {
        Group: WithBoundArgs<typeof _DescriptionGroup, 'striped' | 'compact' | 'cols' | 'layout'>;
        Term: ComponentLike<{
          Args: { cols?: 2 | 3 | 4 | 5 | 6 };
          Element: HTMLElement;
          Blocks: { default: [] };
        }>;
        Details: ComponentLike<{
          Args: { cols?: 2 | 3 | 4 | 5 | 6 };
          Element: HTMLElement;
          Blocks: { default: [] };
        }>;
      },
    ];
  };
  Element: HTMLDListElement;
}

const descriptionListClasses = (opts?: { divided?: boolean; layout?: 'summary' }) =>
  opts?.layout === 'summary'
    ? 'app-description-list--summary'
    : [opts?.divided && 'divide-y tio-divide-subtle'].filter(Boolean).join(' ');

const DescriptionList: TOC<DescriptionListSignature> = <template>
  <dl ...attributes class={{descriptionListClasses (hash divided=@divided layout=@layout)}}>
    {{yield
      (hash
        Group=(component
          _DescriptionGroup striped=@striped compact=@compact cols=@cols layout=@layout
        )
        Term=(component DescriptionTerm cols=@cols layout=@layout)
        Details=(component DescriptionDetails cols=@cols layout=@layout)
      )
    }}
  </dl>
</template>;

export default DescriptionList;
